import React, { useState, useMemo } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { Search, Loader2, ChevronDown, ChevronRight, CheckCircle2, XCircle, Clock, Download } from 'lucide-react';

const cleanCuit = (v) => (v || '').replace(/\D/g, '');
const ACCENT_BTN = {
  slate: 'bg-slate-800 hover:bg-slate-900', sky: 'bg-sky-600 hover:bg-sky-700', rose: 'bg-rose-600 hover:bg-rose-700',
};

export const BulkSearch = ({ accent = 'slate', placeholder, fetchOne, summaryHeaders = [], renderSummaryCells, renderDetail, rowHighlight, onConfigError, exportConfig }) => {
  const [text, setText] = useState('');
  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState({});

  const parsed = useMemo(() => {
    const tokens = (text || '').split(/[\s,;]+/).map(cleanCuit).filter(Boolean);
    const valid = []; const seen = new Set(); let invalid = 0;
    tokens.forEach((t) => {
      if (t.length === 11) { if (!seen.has(t)) { seen.add(t); valid.push(t); } }
      else invalid += 1;
    });
    return { valid, invalid };
  }, [text]);

  const setRow = (cuit, patch) => setRows((prev) => prev.map((r) => (r.cuit === cuit ? { ...r, ...patch } : r)));

  const buscar = async () => {
    if (parsed.valid.length === 0) { toast.error('Pegá al menos un CUIT válido (11 dígitos)'); return; }
    if (parsed.valid.length > 300) { toast.error('Máximo 300 CUIT por búsqueda'); return; }
    const init = parsed.valid.map((c) => ({ cuit: c, status: 'pending', data: null, error: '' }));
    setRows(init); setExpanded({}); setRunning(true);
    let idx = 0;
    const worker = async () => {
      while (idx < init.length) {
        const cuit = init[idx++].cuit;
        setRow(cuit, { status: 'loading' });
        try { const data = await fetchOne(cuit); setRow(cuit, { status: 'ok', data }); }
        catch (e) { setRow(cuit, { status: 'error', error: e.response?.data?.detail || 'Error consultando ARCA' }); }
      }
    };
    await Promise.all(Array.from({ length: Math.min(5, init.length) }, worker));
    setRunning(false);
  };

  const done = rows.filter((r) => r.status === 'ok' || r.status === 'error').length;
  const total = rows.length;
  const okCount = rows.filter((r) => r.status === 'ok').length;
  const errCount = rows.filter((r) => r.status === 'error').length;
  const configErr = rows.some((r) => r.status === 'error' && /certificado|clave|adher|representada/i.test(r.error || ''));

  const exportCsv = () => {
    if (!exportConfig) return;
    const { headers, row } = exportConfig;
    const esc = (v) => { const s = (v === null || v === undefined) ? '' : String(v); return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const lines = [headers.map(esc).join(',')];
    rows.forEach((r) => { lines.push((row(r) || []).map(esc).join(',')); });
    const blob = new Blob(['\ufeff' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `arca_masivo_${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <Card className="p-5 space-y-3">
        <Textarea
          data-testid="bulk-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={6}
          className="font-mono text-sm"
          placeholder={placeholder || `Pegá los CUIT, uno por línea…\n20111111112\n30715619993`}
        />
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm text-slate-500" data-testid="bulk-count">
            {parsed.valid.length} CUIT válido(s){parsed.invalid > 0 ? ` · ${parsed.invalid} ignorado(s)` : ''}
          </p>
          <Button onClick={buscar} disabled={running || parsed.valid.length === 0} className={ACCENT_BTN[accent] || ACCENT_BTN.slate} data-testid="bulk-search-btn">
            {running ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Search size={16} className="mr-2" />}
            {running ? `Buscando… ${done}/${total}` : `Buscar todos${parsed.valid.length ? ` (${parsed.valid.length})` : ''}`}
          </Button>
        </div>
        {total > 0 && (
          <div className="space-y-1" data-testid="bulk-progress">
            <Progress value={total ? Math.round((done / total) * 100) : 0} className="h-2" />
            <p className="text-xs text-slate-400">{done}/{total} procesados · {okCount} ok{errCount ? ` · ${errCount} con error` : ''}</p>
          </div>
        )}
      </Card>

      {configErr && onConfigError && (
        <Card className="p-4 border-amber-200 bg-amber-50">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-amber-800 font-medium">Falta configurar el certificado ARCA.</p>
            <Button variant="outline" size="sm" onClick={onConfigError}>Ir a Configuración</Button>
          </div>
        </Card>
      )}

      {rows.length > 0 && (
        <Card className="p-0 overflow-hidden" data-testid="bulk-results">
          <div className="px-4 py-2 bg-slate-100 flex items-center gap-3 text-xs font-medium text-slate-500">
            <span className="w-4" /><span className="w-6">#</span><span className="w-32 shrink-0">CUIT</span>
            <div className="flex-1 flex gap-4">{summaryHeaders.map((h, i) => <span key={i} className="flex-1">{h}</span>)}</div>
            <span className="w-4" />
          </div>
          {exportConfig && (
            <div className="px-4 py-2 flex justify-end border-b bg-white">
              <Button variant="outline" size="sm" onClick={exportCsv} data-testid="bulk-export-btn"><Download size={14} className="mr-1" /> Exportar CSV ({rows.length})</Button>
            </div>
          )}
          <div className="divide-y">
            {rows.map((row, i) => {
              const isOpen = !!expanded[row.cuit];
              const cells = row.status === 'ok' && renderSummaryCells ? renderSummaryCells(row.data) : null;
              const hl = row.status === 'ok' && rowHighlight ? rowHighlight(row.data) : '';
              return (
                <div key={row.cuit} data-testid={`bulk-wrap-${row.cuit}`}>
                  <div
                    className={`px-4 py-2.5 flex items-center gap-3 ${row.status === 'ok' ? 'cursor-pointer hover:bg-slate-50' : ''} ${hl}`}
                    onClick={() => row.status === 'ok' && setExpanded((p) => ({ ...p, [row.cuit]: !p[row.cuit] }))}
                    data-testid={`bulk-row-${row.cuit}`}
                  >
                    <span className="w-4 shrink-0">{row.status === 'ok' ? (isOpen ? <ChevronDown size={15} className="text-slate-500" /> : <ChevronRight size={15} className="text-slate-400" />) : null}</span>
                    <span className="text-xs text-slate-400 w-6 shrink-0">{i + 1}</span>
                    <span className="font-mono text-sm text-slate-700 w-32 shrink-0">{row.cuit}</span>
                    <div className="flex-1 flex items-center gap-4 min-w-0 text-sm text-slate-600">
                      {row.status === 'ok' && cells && cells.map((c, ci) => <span key={ci} className="flex-1 min-w-0 truncate">{c}</span>)}
                      {row.status === 'error' && <span className="text-amber-700 truncate">{row.error}</span>}
                      {(row.status === 'pending' || row.status === 'loading') && <span className="text-slate-400">{row.status === 'loading' ? 'Consultando…' : 'En cola…'}</span>}
                    </div>
                    <span className="w-4 shrink-0" data-testid={`bulk-status-${row.cuit}`}>
                      {row.status === 'ok' ? <CheckCircle2 size={16} className="text-emerald-500" />
                        : row.status === 'error' ? <XCircle size={16} className="text-rose-500" />
                        : row.status === 'loading' ? <Loader2 size={16} className="animate-spin text-slate-400" />
                        : <Clock size={16} className="text-slate-300" />}
                    </span>
                  </div>
                  {isOpen && row.status === 'ok' && (
                    <div className="px-6 py-3 bg-slate-50/60 border-t" data-testid={`bulk-detail-${row.cuit}`}>
                      {renderDetail ? renderDetail(row.data) : null}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
};

export default BulkSearch;
